import { jwtPayload } from '../../auth';
import { appMetadata } from '../shared/constants';

export const APPLICATION_LIST_PREFERENCES_VERSION = 1;

const STORAGE_KEY_PREFIX = 'matador:applications:list-preferences';

// These values describe the current visit rather than the user's filtered view.
// Keeping every other query parameter makes newly added filters persistent by
// default, without requiring a matching change in this module.
const NON_PERSISTED_PARAMS = ['orgId', 'page', 'pageSize'];

/** The versioned value stored in localStorage for an Applications list view. */
export interface ApplicationListPreferences {
  version: typeof APPLICATION_LIST_PREFERENCES_VERSION;
  // The canonical query string, without the leading "?".
  search: string;
}

interface ApplicationListSearchResolution {
  search: string;
  shouldReplaceUrl: boolean;
}

const asSearchParams = (search: string | URLSearchParams) => new URLSearchParams(search);

/**
 * Scopes preferences to both organization and user so changing accounts in the
 * same browser cannot restore another user's filters. The version in the key
 * also lets a future incompatible schema start with a clean cache.
 */
export const getApplicationListPreferencesKey = (accessToken: string): string => {
  try {
    const claims = jwtPayload(accessToken);
    const userId = claims?.sub;
    const organizationId = claims?.[appMetadata]?.organizationId;

    if (userId && organizationId) {
      return `${STORAGE_KEY_PREFIX}:v${APPLICATION_LIST_PREFERENCES_VERSION}:${encodeURIComponent(organizationId)}:${encodeURIComponent(
        userId,
      )}`;
    }
  } catch {
    // The Applications route is protected, but fall back safely if a malformed token reaches it.
  }

  return `${STORAGE_KEY_PREFIX}:v${APPLICATION_LIST_PREFERENCES_VERSION}:unscoped`;
};

/**
 * Removes navigation-only parameters while preserving all filter/search
 * parameters. This persist-by-default approach is what makes future URL
 * filters sticky automatically.
 */
export const getPersistableApplicationListSearch = (search: string | URLSearchParams): string => {
  const searchParams = asSearchParams(search);
  NON_PERSISTED_PARAMS.forEach(param => searchParams.delete(param));
  return searchParams.toString();
};

/** Returns a safe canonical query string, or an empty string for stale/invalid data. */
export const getSavedApplicationListSearch = (preferences: unknown): string => {
  if (!preferences || typeof preferences !== 'object') return '';

  const savedPreferences = preferences as Partial<ApplicationListPreferences>;
  if (savedPreferences.version !== APPLICATION_LIST_PREFERENCES_VERSION || typeof savedPreferences.search !== 'string') {
    return '';
  }

  return getPersistableApplicationListSearch(savedPreferences.search);
};

export const createApplicationListPreferences = (search: string | URLSearchParams): ApplicationListPreferences => ({
  version: APPLICATION_LIST_PREFERENCES_VERSION,
  search: getPersistableApplicationListSearch(search),
});

// When restoring filters, retain transient parameters from the current URL.
// For example, an orgId used by the organization provider must survive the
// redirect even though it must not become part of the saved preference.
const mergeSavedSearchWithCurrentTransientParams = (savedSearch: string, currentSearch: string): string => {
  const mergedSearchParams = new URLSearchParams(savedSearch);
  const currentSearchParams = new URLSearchParams(currentSearch);

  NON_PERSISTED_PARAMS.forEach(param => {
    currentSearchParams.getAll(param).forEach(value => mergedSearchParams.append(param, value));
  });

  return mergedSearchParams.toString();
};

/**
 * Resolves the initial Applications URL using this precedence:
 *
 * 1. Any incoming filter/search query wins and replaces the saved preference.
 *    This covers Dashboard links and Request Analysis's `analysisStatus=all`.
 * 2. Otherwise, a saved query is restored while current transient parameters
 *    are retained.
 * 3. With neither, the current URL is left unchanged and component defaults apply.
 *
 * `shouldReplaceUrl` tells the route wrapper to perform the restore before the
 * list mounts, keeping the URL as the canonical source of filter state.
 */
export const resolveApplicationListSearch = (currentSearch: string, savedPreferences: unknown): ApplicationListSearchResolution => {
  const incomingApplicationListSearch = getPersistableApplicationListSearch(currentSearch);
  const savedSearch = getSavedApplicationListSearch(savedPreferences);

  if (incomingApplicationListSearch || !savedSearch) {
    return { search: new URLSearchParams(currentSearch).toString(), shouldReplaceUrl: false };
  }

  return {
    search: mergeSavedSearchWithCurrentTransientParams(savedSearch, currentSearch),
    shouldReplaceUrl: true,
  };
};
